import React, { useState, useEffect, useRef  } from 'react';
import { taskService } from 'features/projects/api';
import { Calendar, Edit2, Save, X, Paperclip, User, CheckCircle, Trash2 } from 'lucide-react';
import OrderItem from 'features/crm/components/orders/OrderItem';
import TaskForm from 'features/projects/components/TaskForm';
import { useLayout } from 'shared/contexts/LayoutContext';
import { useStateContext } from 'shared/contexts/ContextProvider';

// Custom hook for shared task functionality
const useTaskOperations = (initialTask, afterSave, afterDelete) => {
  const [task, setTask] = useState(initialTask);
  const { showToast } = useStateContext();
  const { openModal, closeModal } = useLayout();

  const handleEditTask = () => {
    openModal({
      component: (
        <TaskForm 
          task={task} 
          projectId={task.project?.meta?.id}
          afterSave={(updatedTask) => {
            setTask(updatedTask);
            afterSave?.(updatedTask);
          }}
        />
      ),
      title: 'Edit Task',
      confirmCloseText: 'Are you sure you want to close? All unsaved changes will be lost.',
      type: 'right',
    });
  };

  const handleDeleteTask = async () => {
    const confirmDelete = window.confirm(`Are you sure you want to delete task '${task.name}'?`);
    if (confirmDelete) {
      try {
        await taskService.delete(task.id);
        showToast({
          title: 'Success!',
          content: `Task '${task.name}' deleted successfully`,
          type: 'success',
        });
        closeModal?.();
        afterDelete?.(task.id);
      } catch (error) {
        showToast({
          title: 'Error!',
          content: error.message || 'Could not delete task',
          type: 'error',
        });
      }
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const updatedTask = await taskService.update(taskId, { status: newStatus });
      setTask(updatedTask.response);
      afterSave?.(updatedTask);
    } catch (error) {
      showToast({
        title: 'Error!',
        content: error.message || 'Could not update task status',
        type: 'error',
      });
    }
  };

  const handleTargetClick = () => {
    if (task.target) {
      openModal({
        component: <OrderItem item={task.target} />,
        title: 'Task Target',
        type: 'right',
      });
    }
  };

  return {
    task,
    setTask,
    handleEditTask,
    handleDeleteTask,
    handleStatusChange,
    handleTargetClick,
  };
};

// TaskCard component with shared functionality
export const TaskCard = ({ task: initialTask, updateTask, removeTask }) => {
  const { openModal } = useLayout();
  const {
    task,
    handleEditTask,
    handleDeleteTask,
    handleStatusChange,
  } = useTaskOperations(initialTask, updateTask, removeTask);

  const isDone = task.status === 'done';

  const handleSelect = () => {
    openModal({
      component: (
        <TaskItem
          item={task}
          afterSave={updateTask}
          afterDelete={removeTask}
        />
      ),
      title: task.name,
    });
  };

  return (
    <div
      className={`group p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md relative
        ${isDone ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}
      onClick={handleSelect}
    >
      <div className='absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleEditTask();
          }}
          className='p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded'
        >
          <Edit2 className='w-4 h-4' />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteTask();
          }}
          className='p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded'
        >
          <Trash2 className='w-4 h-4' />
        </button>
      </div>

      <div className='flex items-start gap-3'>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleStatusChange(task.id, isDone ? 'todo' : 'done');
          }}
          className={`mt-1 ${isDone ? 'text-green-500' : 'text-gray-400'} hover:scale-110 transition-transform`}
        >
          <CheckCircle className='w-5 h-5' />
        </button>

        <div className='flex-1 min-w-0'>
          <div className='flex items-start justify-between'>
            <h4 className={`font-medium truncate ${isDone ? 'text-green-700' : 'text-gray-900'}`}>
              {task.name}
            </h4>
          </div>

          <p className={`mt-1 text-sm line-clamp-2 ${isDone ? 'text-green-600' : 'text-gray-600'}`}>
            {task.text}
          </p>

          <div className='flex items-center gap-4 mt-2 text-xs text-gray-500'>
            <div className='flex items-center'>
              <Calendar className='w-4 h-4 mr-1' />
              {task.deadline ? new Date(task.deadline).toLocaleDateString() : 'No deadline'}
            </div>
            <div className='flex items-center'>
              <User className='w-4 h-4 mr-1' />
              {task.author.meta.name}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// TaskItem component with shared functionality
export const TaskItem = ({ item, afterSave, afterDelete }) => {
  const {
    task,
    handleEditTask,
    handleDeleteTask,
    handleTargetClick,
  } = useTaskOperations(item, afterSave, afterDelete);

  return (
    <div className='flex-1 flex flex-col h-full min-h-0'>
      <div className='p-4 space-y-4 border-b'>
        <div className='flex items-center justify-between'>
          <h2 className='text-lg font-bold'>{task.name}</h2>
          <div className='flex items-center gap-2'>
            <button
              onClick={handleEditTask}
              className='p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100'
            >
              <Edit2 className='w-5 h-5' />
            </button>
            <button
              onClick={handleDeleteTask}
              className='p-2 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50'
            >
              <Trash2 className='w-5 h-5' />
            </button>
          </div>
        </div>

        <p className='text-sm text-gray-600'>{task.text}</p>

        <div className='flex items-center justify-between text-sm'>
          <div className='flex items-center gap-2'>
            <Calendar className='w-4 h-4 text-gray-500' />
            <span className='text-gray-600'>
              {task.deadline ? new Date(task.deadline).toLocaleString() : 'No deadline'}
            </span>
          </div>
          {task.target && (
            <button
              onClick={handleTargetClick}
              className='flex items-center gap-2 text-blue-500 hover:text-blue-600'
            >
              <Paperclip className='w-4 h-4' />
              <span>{task.target.meta.name}</span>
            </button>
          )}
        </div>
      </div>

      <TaskNotes task={task} />
    </div>
  );
};

const TaskNotes = ({ task }) => {
    const [notes, setNotes] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [editingNote, setEditingNote] = useState(null);
    const [editText, setEditText] = useState('');
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const { user, token, showToast } = useStateContext();
    const [offset, setOffset] = useState(0);
    const limit = 10;
    const messagesContainerRef = useRef(null);

    const fetchNotes = async (append = false) => {
        try {
            setLoading(true);
            const response = await taskService.getNotes(
                task.id, 
                `?limit=${limit}&offset=${append ? offset + limit : 0}&order=created,asc`, 
                token
            );
            const newNotes = response.response.rows || [];
            
            if (append) {
                // When appending, add new notes to the existing ones
                setNotes(prev => {
                    const combined = [...prev, ...newNotes];
                    // Remove duplicates based on note ID
                    const uniqueNotes = Array.from(
                        new Map(combined.map(note => [note.id, note])).values()
                    );
                    // Sort by creation date
                    return uniqueNotes.sort((a, b) => 
                        new Date(a.created) - new Date(b.created)
                    );
                });
                setOffset(prev => prev + limit);
            } else {
                setNotes((prev) => {
                    return Array.from(new Map([...newNotes, ...prev].map(note => [note.id, note])).values()).sort((a, b) => new Date(a.created) - new Date(b.created));
                }); 
                setOffset(notes.length);
            }
            
            setHasMore(newNotes.length === limit);
        } catch (error) {
            showToast({
                title: 'Error!',
                content: error.message || 'Could not fetch notes',
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const scrollToBottom = () => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [notes]);
    
    useEffect(() => {
        fetchNotes();
        // Set up auto-update interval for first page
        const intervalId = setInterval(() => {
            if (offset <= limit) { // Only update if on first page
                fetchNotes();
            }
        }, 60000);

        return () => clearInterval(intervalId);
    }, [task]);


    const handleLoadMore = () => {
        fetchNotes(true);
    };

    const handleEdit = (note) => {
        setEditingNote(note.id);
        setEditText(note.text);
    };

    const handleSaveEdit = async () => {
        if (!editText.trim()) return;
        
        try {
            await taskService.updateNote(task.id, editingNote, editText, token);
            setNotes(prevNotes => 
                prevNotes.map(note => 
                    note.id === editingNote 
                        ? { ...note, text: editText }
                        : note
                )
            );
            setEditingNote(null);
            showToast({
                title: 'Success!',
                content: 'Note updated successfully',
                type: 'success'
            });
        } catch (error) {
            showToast({
                title: 'Error!',
                content: error.message || 'Could not update note',
                type: 'error'
            });
        }
    };

    const handleDelete = async (noteId) => {
        if (!window.confirm('Are you sure you want to delete this note?')) return;
        
        try {
            await taskService.deleteNote(task.id, noteId, token);
            setNotes(prevNotes => prevNotes.filter(note => note.id !== noteId));
            showToast({
                title: 'Success!',
                content: 'Note deleted successfully',
                type: 'success'
            });
        } catch (error) {
            showToast({
                title: 'Error!',
                content: error.message || 'Could not delete note',
                type: 'error'
            });
        }
    };

    return (    
        <div className='flex-1 flex flex-col min-h-0 max-h-[calc(100vh-10px)]'>
            {/* Messages Container */}
            <div
                ref={messagesContainerRef}
                className='flex-1 p-4 space-y-4 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100'
                style={{ scrollbarWidth: 'thin', scrollbarColor: '#d4d4d4 #f3f4f6', maxHeight: 500 }}
            >   
                {/* Load More Button */}
                <div className='p-2 text-center border-b'>
                    {loading ? (
                        <div className='text-sm text-gray-500'>Loading...</div>
                    ) : hasMore ? (
                        <button
                            onClick={handleLoadMore}
                            className='text-sm text-blue-500 hover:text-blue-600'
                        >
                            Load more messages
                        </button>
                    ) : notes.length === 0 ? (
                        <div className='text-sm text-gray-500'>No messages yet</div>
                    ) : (
                        <div className='text-sm text-gray-500'>No more messages</div>
                    )}
                </div>

                {/* Messages */}
                {notes.map(note => (
                    <div 
                        key={note.id}
                        className={`flex ${note.author.meta.id === user.id ? 'justify-end' : 'justify-start'}`}
                    >
                        <div 
                            className={`max-w-[80%] p-3 rounded-lg ${
                                note.author.meta.id === user.id 
                                    ? 'bg-blue-500 text-white ml-8' 
                                    : 'bg-gray-100 text-gray-700 mr-8'
                            }`}
                        >
                            <div className='flex tasks-center justify-between gap-4 mb-1'>
                                <span className='text-xs font-medium'>
                                    {note.author.meta.name}
                                </span>
                                <span className='text-xs opacity-75'>
                                    {new Date(note.created).toLocaleString()}
                                </span>
                            </div>
                            
                            {editingNote === note.id ? (
                                <div className='mt-2 space-y-2'>
                                    <textarea
                                        value={editText}
                                        onChange={(e) => setEditText(e.target.value)}
                                        className='w-full p-2 text-sm text-gray-900 border rounded resize-none focus:outline-none focus:ring-2 focus:ring-blue-500'
                                        rows={2}
                                    />
                                    <div className='flex justify-end gap-2'>
                                        <button
                                            onClick={() => setEditingNote(null)}
                                            className='p-1 text-sm text-gray-500 hover:text-gray-700'
                                        >
                                            <X className='w-4 h-4' />
                                        </button>
                                        <button
                                            onClick={handleSaveEdit}
                                            className='p-1 text-sm text-green-500 hover:text-green-700'
                                        >
                                            <Save className='w-4 h-4' />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <p className='text-sm'>{note.text}</p>
                                    {note.author.meta.id === user.id && (
                                        <div className='flex justify-end gap-2 mt-2'>
                                            <button
                                                onClick={() => handleEdit(note)}
                                                className={`p-1 ${
                                                    note.author.meta.id === user.id 
                                                        ? 'text-white/70 hover:text-white' 
                                                        : 'text-gray-500 hover:text-gray-700'
                                                }`}
                                            >
                                                <Edit2 className='w-4 h-4' />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(note.id)}
                                                className={`p-1 ${
                                                    note.author.meta.id === user.id 
                                                        ? 'text-white/70 hover:text-white' 
                                                        : 'text-gray-500 hover:text-gray-700'
                                                }`}
                                            >
                                                <Trash2 className='w-4 h-4' />
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Input Form */}
            <form 
                onSubmit={async (e) => {
                    e.preventDefault();
                    if (!newNote.trim()) return;

                    try {
                        await taskService.addNote(task.id, newNote, token);
                        setNewNote('');
                        fetchNotes(); // Refresh the latest messages
                    } catch (error) {
                        showToast({
                            title: 'Error!',
                            content: error.message || 'Could not add note',
                            type: 'error'
                        });
                    }
                }} 
                className='p-4 border-t bg-white mt-auto min-h-0'
            >
                <div className='flex gap-2'>
                    <input
                        type='text'
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder='Write a comment...'
                        className='flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                    />
                    <button 
                        type='submit'
                        className='px-4 py-2 text-sm text-white bg-blue-500 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500'
                    >
                        Send
                    </button>
                </div>
            </form>
        </div>
    );
};
